import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Send, Eye, Mail, History, FileText, Save, CheckCircle2, XCircle, Star } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';

/**
 * AdminEmails — Stage 3 email center. Three panels:
 *   1. Compose  — pick customer + template → preview → send
 *   2. History  — last 100 sends with status + optional error
 *   3. Templates — edit subject/body of the 10 canned templates
 */
const AdminEmails = ({ onUnauthorized }) => {
  return (
    <Tabs defaultValue="compose" className="w-full" data-testid="admin-emails">
      <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto mb-4">
        <TabsTrigger value="compose" data-testid="em-tab-compose" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><Mail className="w-4 h-4 mr-2" /> Compose</TabsTrigger>
        <TabsTrigger value="history" data-testid="em-tab-history" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><History className="w-4 h-4 mr-2" /> History</TabsTrigger>
        <TabsTrigger value="templates" data-testid="em-tab-templates" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><FileText className="w-4 h-4 mr-2" /> Templates</TabsTrigger>
      </TabsList>
      <TabsContent value="compose"><ComposePanel onUnauthorized={onUnauthorized} /></TabsContent>
      <TabsContent value="history"><HistoryPanel onUnauthorized={onUnauthorized} /></TabsContent>
      <TabsContent value="templates"><TemplatesPanel onUnauthorized={onUnauthorized} /></TabsContent>
    </Tabs>
  );
};

// ------- Compose -------
const ComposePanel = ({ onUnauthorized }) => {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templateKey, setTemplateKey] = useState('google_review_request');
  const [customerEmail, setCustomerEmail] = useState('');
  const [manualName, setManualName] = useState('');
  const [servicePerformed, setServicePerformed] = useState('');
  const [subjectOverride, setSubjectOverride] = useState('');
  const [bodyOverride, setBodyOverride] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tpls, cust] = await Promise.all([
          axios.get(`${API}/api/admin/email-templates`, { headers: authHeaders() }),
          axios.get(`${API}/api/admin/customers`, { headers: authHeaders() }),
        ]);
        setTemplates(tpls.data || []);
        setCustomers((cust.data || []).filter((c) => c.email && !c.do_not_contact));
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
      }
    })();
  }, [onUnauthorized]);

  const currentTpl = useMemo(() => templates.find((t) => t.key === templateKey), [templates, templateKey]);
  const [firstName, lastName] = useMemo(() => {
    const found = customers.find((c) => c.email === customerEmail);
    if (found?.full_name) {
      const [f, ...rest] = found.full_name.split(' ');
      return [f, rest.join(' ')];
    }
    const [f, ...rest] = manualName.split(' ');
    return [f, rest.join(' ')];
  }, [customers, customerEmail, manualName]);

  const runPreview = async () => {
    if (!customerEmail) { toast.error('Pick or type a customer email'); return; }
    setLoadingPreview(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/emails/preview`, {
        customer_email: customerEmail,
        template_key: templateKey,
        first_name: firstName,
        last_name: lastName,
        service_performed: servicePerformed || undefined,
        subject_override: subjectOverride || undefined,
        body_override: bodyOverride || undefined,
      }, { headers: authHeaders() });
      setPreview(data);
    } catch (err) { toast.error(`Preview failed: ${apiErr(err)}`); }
    finally { setLoadingPreview(false); }
  };

  const doSend = async () => {
    if (!customerEmail) { toast.error('Pick a customer email'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/admin/emails/send`, {
        customer_email: customerEmail,
        template_key: templateKey,
        first_name: firstName,
        last_name: lastName,
        service_performed: servicePerformed || undefined,
        subject_override: subjectOverride || undefined,
        body_override: bodyOverride || undefined,
      }, { headers: authHeaders() });
      toast.success(`Email sent to ${customerEmail}`);
      setPreview(null);
      setSubjectOverride(''); setBodyOverride('');
    } catch (err) { toast.error(`Send failed: ${apiErr(err)}`); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Compose email</CardTitle>
          <p className="text-xs text-gray-400">Pick a customer, choose a template, preview it filled in with real info, and send. Google Review Request sends a big button linking to your review URL.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest">Customer</Label>
              <select data-testid="em-compose-customer" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded px-3 py-2 text-sm">
                <option value="">— Select from list —</option>
                {customers.map((c) => (
                  <option key={c.email} value={c.email}>{c.full_name || c.email} · {c.email}</option>
                ))}
              </select>
              <div className="mt-2">
                <Label className="text-gray-500 text-[10px] uppercase tracking-widest">Or type an email manually</Label>
                <div className="flex gap-2">
                  <Input data-testid="em-compose-email" type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="name@example.com" className="bg-slate-800 border-slate-700 text-white" />
                  <Input data-testid="em-compose-name" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Full name (optional)" className="bg-slate-800 border-slate-700 text-white flex-1" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest">Template</Label>
              <select data-testid="em-compose-template" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded px-3 py-2 text-sm">
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <div className="mt-2">
                <Label className="text-gray-500 text-[10px] uppercase tracking-widest">Service performed (optional)</Label>
                <Input data-testid="em-compose-service" value={servicePerformed} onChange={(e) => setServicePerformed(e.target.value)} placeholder="Cerakote job / Trigger install / etc." className="bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
          </div>

          <details className="border border-slate-800 rounded-md p-3 bg-slate-950/40">
            <summary className="text-gray-300 text-sm cursor-pointer">Optional: edit subject &amp; message for this send only</summary>
            <div className="mt-3 space-y-2">
              <Input data-testid="em-compose-subject-override" value={subjectOverride} onChange={(e) => setSubjectOverride(e.target.value)} placeholder={currentTpl?.subject || 'Custom subject line'} className="bg-slate-800 border-slate-700 text-white" />
              <Textarea data-testid="em-compose-body-override" value={bodyOverride} onChange={(e) => setBodyOverride(e.target.value)} rows={5} placeholder={currentTpl?.body?.slice(0, 80) + '...' || 'Custom message body'} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
              <p className="text-[10px] text-gray-500">You can still use {'{{first_name}}'}, {'{{business_phone}}'}, {'{{google_review_url}}'} etc. — they'll be filled in automatically.</p>
            </div>
          </details>

          <div className="flex gap-2 justify-end">
            <Button data-testid="em-preview-btn" onClick={runPreview} disabled={loadingPreview} variant="outline" className="border-slate-700 text-white hover:bg-slate-800">
              <Eye className="w-4 h-4 mr-2" /> {loadingPreview ? 'Loading…' : 'Preview'}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="em-send-btn" disabled={sending || !customerEmail} className="bg-red-600 hover:bg-red-700 text-white">
                  <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : 'Send email'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Send this email?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    We'll send <strong>{currentTpl?.label || templateKey}</strong> to <strong className="font-mono">{customerEmail}</strong>. It will be logged in the History tab.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="em-send-confirm" onClick={doSend} className="bg-red-600 hover:bg-red-700 text-white">Send it</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card className="bg-slate-900 border-slate-800" data-testid="em-preview-card">
          <CardHeader>
            <CardTitle className="text-white">Preview — {preview.template_label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="bg-slate-950/50 border border-slate-800 rounded p-3">
              <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Subject</div>
              <div className="text-white font-semibold">{preview.subject}</div>
            </div>
            <div className="bg-slate-950/50 border border-slate-800 rounded p-3 whitespace-pre-wrap text-sm text-gray-200 leading-relaxed">
              {preview.body}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ------- History -------
const HistoryPanel = ({ onUnauthorized }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/emails/history?limit=100`, { headers: authHeaders() });
        setEntries(data || []);
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
      } finally { setLoading(false); }
    })();
  }, [onUnauthorized]);

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;
  if (!entries.length) return <p className="text-gray-500 text-sm">No emails sent yet.</p>;

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white">Email history</CardTitle>
        <p className="text-xs text-gray-400">Last 100 emails sent (or attempted). Failures show a reason.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {entries.map((e) => (
          <div key={e.id} data-testid={`em-history-${e.id}`} className="grid grid-cols-12 gap-2 bg-slate-950/40 border border-slate-800 rounded p-2 items-center">
            <div className="col-span-1">
              {e.status === 'sent'
                ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                : <XCircle className="w-5 h-5 text-red-400" />}
            </div>
            <div className="col-span-4 min-w-0">
              <div className="text-white text-sm truncate">{e.customer_name || e.customer_email}</div>
              <div className="text-[11px] text-gray-500 font-mono truncate">{e.customer_email}</div>
            </div>
            <div className="col-span-4 min-w-0">
              <div className="text-gray-300 text-sm truncate">{e.template_label}</div>
              <div className="text-[11px] text-gray-500 truncate">{e.subject}</div>
              {e.error && <div className="text-[11px] text-red-400 truncate">{e.error}</div>}
            </div>
            <div className="col-span-3 text-right text-[11px] text-gray-500 font-mono">{new Date(e.sent_at).toLocaleString()}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ------- Templates -------
const TemplatesPanel = ({ onUnauthorized }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/email-templates`, { headers: authHeaders() });
      setTemplates(data || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const draftFor = (key) => drafts[key] || {};
  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  const save = async (key) => {
    const patch = drafts[key];
    if (!patch) return;
    setSavingKey(key);
    try {
      await axios.patch(`${API}/api/admin/email-templates/${key}`, patch, { headers: authHeaders() });
      toast.success('Template updated');
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      load();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally { setSavingKey(null); }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;

  return (
    <div className="space-y-3" data-testid="em-templates-panel">
      <p className="text-xs text-gray-400 px-1">Edit the subject line and message body of any of the 10 canned templates. Variables like <span className="font-mono text-amber-300">{'{{first_name}}'}</span> and <span className="font-mono text-amber-300">{'{{business_phone}}'}</span> are filled in automatically when you send.</p>
      {templates.map((t) => {
        const d = draftFor(t.key);
        const dirty = Object.keys(d).length > 0;
        const subject = d.subject !== undefined ? d.subject : t.subject;
        const body = d.body !== undefined ? d.body : t.body;
        return (
          <Card key={t.key} className="bg-slate-900 border-slate-800" data-testid={`em-tpl-${t.key}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                {t.key === 'google_review_request' && <Star className="w-4 h-4 text-amber-400" />}
                {t.label}
              </CardTitle>
              <p className="text-[10px] text-gray-500 font-mono">{t.key}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input data-testid={`em-tpl-subject-${t.key}`} value={subject} onChange={(e) => setDraft(t.key, { subject: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
              <Textarea data-testid={`em-tpl-body-${t.key}`} value={body} onChange={(e) => setDraft(t.key, { body: e.target.value })} rows={6} className="bg-slate-800 border-slate-700 text-white font-mono text-xs" />
              <div className="flex justify-end">
                <Button data-testid={`em-tpl-save-${t.key}`} onClick={() => save(t.key)} disabled={!dirty || savingKey === t.key} size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                  <Save className="w-3 h-3 mr-2" /> {savingKey === t.key ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminEmails;
